import "../styles/CommitDetails.css";
import React, { useState, useEffect } from "react";
import { useLoaderData } from "react-router-dom";
import { ApiHelper } from '../ApiHelper';
import { MetricCPU, ComponentGraphs, OVERALL, ComponentGraphNames } from "../utils/constants";
import { useLocation, useNavigate } from 'react-router-dom';
import { ComponentDetailsGraph } from './ComponentDetailsGraph.tsx';
import { Divider } from '@fluentui/react-components';
import { useConfig } from "../configContext";
import { LabeledSelect, LabeledDateInput } from './common/FormControls.tsx';
import type { Environment, Process, Service } from '../interfaces/Service';

// ===== Types =====

type NavState = {
    startDate: Date | null;
    endDate: Date | null;
    metricType: number;
    process: number;
};

type LoaderData = {
    componentsDetails: any[] | null;
    componentId: string;
    serviceName: string;
    errorMessage: string | null;
};

type FilterParams = {
    env: number;
    metricType: number;
    process: number;
    graph: number;
    startDate: Date | null;
    endDate: Date | null;
};

type SelectOption = {
    key: number;
    text: string;
};

type ComponentDetailsCardProps = {
    componentsDetails: any[] | null;
    serviceName: string;
    componentId: string;
};

// ===== Main Component =====

export function ComponentDetails() {
    const { componentsDetails, componentId, serviceName, errorMessage } = useLoaderData() as LoaderData;
    useEffect(() => { document.title = 'PR Guardian - ' + componentId; }, [componentId]);
    return (
        <div className="page-container">
            {errorMessage ? (
                <div className="no-commits-message">{errorMessage}</div>
            ) : (<ComponentDetailsCard componentsDetails={componentsDetails} serviceName={serviceName} componentId={componentId} />)}
        </div>
    );
}

// ===== Loader =====

let cachedComponentUrl: string | null = null;
let cachedComponent: any[] | null = null;

export async function ComponentDetailsLoader(loaderArgs: { request: Request; params: { componentId: string; serviceName: string } }) {
    const url = new URL(loaderArgs.request.url);
    const componentId = loaderArgs.params.componentId;
    const serviceName = loaderArgs.params.serviceName;
    const defaultStart = new Date();
    defaultStart.setMonth(defaultStart.getMonth() - 1);
    const navState: NavState = {
        startDate: url.searchParams.get('startDate') ? new Date(url.searchParams.get('startDate')!) : defaultStart,
        endDate: url.searchParams.get('endDate') ? new Date(url.searchParams.get('endDate')!) : new Date(),
        metricType: url.searchParams.get('metricType') ? Number(url.searchParams.get('metricType')) : 0,
        process: url.searchParams.get('process') ? Number(url.searchParams.get('process')) : 0
    };
    const apiUrl = `/api/service/${serviceName}/components/${componentId}/details?` + new URLSearchParams({
        startDate: navState.startDate ? navState.startDate.toISOString() : '',
        endDate: navState.endDate ? navState.endDate.toISOString() : '',
        metricType: navState.metricType.toString(),
        process: navState.process.toString()
    }).toString();

    let componentsDetails: any[] | null = null;
    let errorMessage: string | null = null;

    try {
        if (cachedComponentUrl !== apiUrl) {
            componentsDetails = await ApiHelper<any[]>(apiUrl);
            cachedComponent = componentsDetails;
            cachedComponentUrl = apiUrl;
        } else {
            componentsDetails = cachedComponent;
        }
    } catch (error) {
        errorMessage = 'Oops, sorry. There are no components to see :/';
    }

    return { componentsDetails, componentId, serviceName, errorMessage };
}

// ===== Details Card =====

function ComponentDetailsCard({ componentsDetails, serviceName, componentId }: ComponentDetailsCardProps) {
    const config = useConfig();
    const service = config.services.find((candidate: Service) => candidate.name === serviceName);
    const serviceEnvironments: Environment[] = service?.Environments ?? Object.values(service?.environments ?? {});
    const processes: Process[] = service?.Processes ?? Object.values(service?.processes ?? {});
    const environmentIds: number[] = serviceEnvironments.map((env) => env.id);
    const lowestEnvId = Math.min(...environmentIds, 0);

    const location = useLocation();
    const navigate = useNavigate();

    const getInitialState = (): FilterParams => {
        const qs = new URLSearchParams(location.search);
        const state: any = location.state || {};
        return {
            env: state.env ?? (qs.get('env') ? Number(qs.get('env')) : lowestEnvId),
            metricType: state.metricType ?? (qs.get('metricType') ? Number(qs.get('metricType')) : MetricCPU),
            process: state.process ?? (qs.get('process') ? Number(qs.get('process')) : 0),
            graph: state.graph ?? (qs.get('graph') ? Number(qs.get('graph')) : OVERALL),
            startDate: state.startDate ?? (qs.get('startDate') ? new Date(qs.get('startDate')!) : (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d; })()),
            endDate: state.endDate ?? (qs.get('endDate') ? new Date(qs.get('endDate')!) : new Date()),
        };
    };

    const [params, setParams] = useState<FilterParams>(getInitialState);

    const updateParams = (updates: Partial<FilterParams>) => {
        const newParams = { ...params, ...updates };
        setParams(newParams);
        const qs = new URLSearchParams();
        qs.set('env', String(newParams.env));
        qs.set('metricType', String(newParams.metricType));
        qs.set('process', String(newParams.process));
        if (newParams.graph !== OVERALL) qs.set('graph', String(newParams.graph));
        if (newParams.startDate) qs.set('startDate', newParams.startDate.toISOString());
        if (newParams.endDate) qs.set('endDate', newParams.endDate.toISOString());
        navigate(location.pathname + '?' + qs.toString(), { state: newParams });
    };

    const handleChange = (key: keyof FilterParams) => (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const value = key === 'startDate' || key === 'endDate'
            ? new Date(e.target.value)
            : Number(e.target.value);
        updateParams({ [key]: value });
    };

    const selectedProcess = processes.find((process) => process.id === params.process);
    const availableMetrics = selectedProcess ? Object.values(selectedProcess.metrics) : [];
    const componentDetails = componentsDetails?.find((c: any) => c.commitId === componentId);

    const envOptions: SelectOption[] = serviceEnvironments.map((env) => ({
        key: env.id,
        text: env.name,
    }));
    const processOptions: SelectOption[] = processes.map((process) => ({
        key: process.id,
        text: process.name,
    }));
    const metricOptions: SelectOption[] = availableMetrics.map((metrics) => ({
        key: metrics.id,
        text: metrics.name,
    }));
    const graphOptions: SelectOption[] = ComponentGraphs.map((m: number) => ({
        key: m,
        text: ComponentGraphNames[m],
    }));

    return (
        <div className="farmCard">
            <div className="flex-container">
                <LabeledSelect label="Environment:" value={params.env} onChange={handleChange('env')} options={envOptions} />
                <LabeledSelect label="Process:" value={params.process} onChange={handleChange('process')} options={processOptions} />
                <LabeledSelect label="Metric:" value={params.metricType} onChange={handleChange('metricType')} options={metricOptions} />
                <LabeledDateInput label="Start Date:" value={params.startDate} onChange={handleChange('startDate')} />
                <LabeledDateInput label="End Date:" value={params.endDate} onChange={handleChange('endDate')} />
                <LabeledSelect label="Graph:" value={params.graph} onChange={handleChange('graph')} options={graphOptions} />
            </div>
            <Divider style={{ padding: '5px' }} />
            <ComponentDetailsGraph serviceId={service?.id ?? 0} env={params.env} metricType={params.metricType} process={params.process} type={params.graph} commitDetails={componentDetails} />
        </div>
    );
}
